package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.theme.EffectiveTheme
import co.maytes.standupbot.dashboard.features.theme.LocalThemeStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.ThemeScheme
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.dom.Div

@Composable
fun rememberGalleryScheme(): ThemeScheme {
    val store = LocalThemeStore.current
    val preference by store.preference
    return remember(preference) {
        if (store.effectiveTheme() == EffectiveTheme.Dark) ThemeScheme.Dark else ThemeScheme.Light
    }
}

@Composable
fun GallerySection(
    id: String,
    title: String,
    description: String? = null,
    content: @Composable () -> Unit,
) {
    Stack(
        gap = 20,
        modifier = Modifier
            .id(id)
            .styleModifier {
                property("scroll-margin-top", "72px")
                property("padding-bottom", "40px")
                property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}")
            },
    ) {
        Stack(6) {
            SpanText(
                text = title,
                modifier = Modifier.styleModifier {
                    property("font-size", "${Typography.h2.size}")
                    property("line-height", "${Typography.h2.lineHeight}")
                    property("font-weight", "${Typography.h2.weight}")
                    property("color", Palette.textPrimary.cssRgb())
                },
            )
            if (description != null) GalleryNote(description)
        }
        content()
    }
}

@Composable
fun Stack(gap: Int, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Div(
        attrs = modifier
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "${gap}px")
                property("min-width", "0")
            }
            .toAttrs(),
    ) {
        content()
    }
}

@Composable
fun GalleryNote(text: String) {
    SpanText(
        text = text,
        modifier = Modifier.styleModifier {
            property("font-size", "${Typography.bodySmall.size}")
            property("line-height", "${Typography.bodySmall.lineHeight}")
            property("color", Palette.textSecondary.cssRgb())
            property("max-width", "72ch")
        },
    )
}

@Composable
fun GalleryHeading(text: String) {
    SpanText(
        text = text,
        modifier = Modifier.styleModifier {
            property("font-size", "${Typography.label.size}")
            property("font-weight", "600")
            property("color", Palette.textPrimary.cssRgb())
        },
    )
}

@Composable
fun GalleryMono(text: String, color: CSSColorValue = Palette.textSecondary) {
    SpanText(
        text = text,
        modifier = Modifier.styleModifier {
            property("font-family", Typography.FONT_MONO)
            property("font-size", "${Typography.caption.size}")
            property("color", color.cssRgb())
            property("font-variant-numeric", "tabular-nums")
        },
    )
}

@Composable
fun GalleryGrid(minColumn: Int, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fill, minmax(${minColumn}px, 1fr))")
                property("gap", "16px")
            }
        },
    ) {
        content()
    }
}

@Composable
fun GalleryPanel(content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                property("background-color", Palette.surface.cssRgb())
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("border-radius", "12px")
                property("padding", "16px")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "12px")
                property("min-width", "0")
            }
        },
    ) {
        content()
    }
}

@Composable
fun GalleryMatrix(
    component: String,
    variants: List<String>,
    sizes: List<String>,
    states: List<GalleryState> = GalleryState.entries,
    note: String? = null,
    columnMin: Int = 132,
    cell: @Composable (variant: String, size: String, state: GalleryState) -> Unit,
) {
    Stack(10) {
        GalleryHeading(component)
        if (note != null) GalleryNote(note)
        Div(
            attrs = {
                style {
                    property("overflow-x", "auto")
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("border-radius", "12px")
                    property("background-color", Palette.surface.cssRgb())
                }
            },
        ) {
            Div(
                attrs = {
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "132px repeat(${states.size}, minmax(${columnMin}px, 1fr))")
                        property("min-width", "${132 + states.size * columnMin}px")
                        property("width", "100%")
                    }
                },
            ) {
                MatrixHeaderCell("")
                states.forEach { MatrixHeaderCell(it.label) }
                variants.forEach { variant ->
                    sizes.forEach { size ->
                        MatrixRowLabel(if (sizes.size > 1) "$variant · $size" else variant)
                        states.forEach { state -> StateCell(state) { cell(variant, size, state) } }
                    }
                }
            }
        }
    }
}

@Composable
private fun MatrixHeaderCell(text: String) {
    Div(
        attrs = {
            style {
                property("padding", "8px 12px")
                property("background-color", Palette.surfaceSubtle.cssRgb())
                property("border-bottom", "1px solid ${Palette.border.cssRgb()}")
            }
        },
    ) {
        GalleryMono(text)
    }
}

@Composable
private fun MatrixRowLabel(text: String) {
    Div(
        attrs = {
            style {
                property("padding", "8px 12px")
                property("display", "flex")
                property("align-items", "center")
                property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
            }
        },
    ) {
        GalleryMono(text)
    }
}

class KeyBinding(val keys: List<String>, val action: String)

fun keys(action: String, vararg keys: String) = KeyBinding(keys.toList(), action)

@Composable
fun KeyboardChecklist(component: String, bindings: List<KeyBinding>) {
    GalleryPanel {
        GalleryHeading("Keyboard checklist: $component")
        Div(
            attrs = {
                attr("role", "list")
                attr("aria-label", "$component keyboard checklist")
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "8px")
                }
            },
        ) {
            bindings.forEach { binding ->
                Div(
                    attrs = {
                        attr("role", "listitem")
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "minmax(96px, 150px) 1fr")
                            property("align-items", "start")
                            property("gap", "6px 12px")
                        }
                    },
                ) {
                    Div(
                        attrs = {
                            style {
                                property("display", "flex")
                                property("flex-wrap", "wrap")
                                property("gap", "4px")
                            }
                        },
                    ) {
                        binding.keys.forEach { KeyCap(it) }
                    }
                    GalleryNote(binding.action)
                }
            }
        }
    }
}

@Composable
private fun KeyCap(text: String) {
    Div(
        attrs = {
            style {
                property("padding", "2px 8px")
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("border-bottom-width", "2px")
                property("border-radius", "6px")
                property("background-color", Palette.surfaceSubtle.cssRgb())
            }
        },
    ) {
        GalleryMono(text, Palette.textPrimary)
    }
}

@Composable
fun GalleryRow(gap: Int = 16, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "flex-start")
                property("gap", "${gap}px")
            }
        },
    ) {
        content()
    }
}
